import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { getCompanyNameValidator, getUSPhoneNumberValidator } from 'validators';
import { TextField, USPhoneNumberInput } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import PostalAddressForm from '~/components/PostalAddressForm/PostalAddressForm';
import { useLocaleKeys } from '~/i18n';
import styles from './BusinessDetailsAStep.module.scss';
export const GetBusinessDetailsAStepTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const businessDetailsAStepTextsPath = 'vendorOnboarding.businessDetailsAStep';
const validatePhone = getUSPhoneNumberValidator();
export const BusinessDetailsAStep = (param)=>{
    let { testId, values, onChange, onContinue, submitRef } = param;
    const testIds = useMemo(()=>GetBusinessDetailsAStepTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys(businessDetailsAStepTextsPath);
    const [showErrors, setShowErrors] = useState(false);
    const methods = useForm({
        mode: 'onChange',
        defaultValues: {
            postalAddress: values.postalAddress,
            phoneNumber: values.phoneNumber
        }
    });
    const watched = methods.watch();
    const valuesRef = useRef(values);
    useEffect(()=>{
        valuesRef.current = values;
    }, [
        values
    ]);
    useEffect(()=>{
        const subscription = methods.watch((value, info)=>{
            var _value_phoneNumber;
            if (info.name == null) return;
            onChange({
                companyName: valuesRef.current.companyName,
                postalAddress: value.postalAddress,
                phoneNumber: (_value_phoneNumber = value.phoneNumber) !== null && _value_phoneNumber !== void 0 ? _value_phoneNumber : ''
            });
        });
        return ()=>subscription.unsubscribe();
    }, [
        methods,
        onChange
    ]);
    const validateCompanyName = useMemo(()=>getCompanyNameValidator(undefined, t.companyNameRequired()), [
        t
    ]);
    const errors = useMemo(()=>{
        const result = {};
        const companyNameResult = validateCompanyName(values.companyName);
        if (companyNameResult !== true) {
            result.companyName = typeof companyNameResult === 'string' ? companyNameResult : t.companyNameRequired();
        }
        if (!watched.phoneNumber || watched.phoneNumber === '+1') {
            result.phoneNumber = t.phoneRequired();
        } else if (validatePhone(watched.phoneNumber) !== true) {
            result.phoneNumber = t.phoneInvalid();
        }
        return result;
    }, [
        validateCompanyName,
        values.companyName,
        watched,
        t
    ]);
    const handleSubmit = useCallback(()=>{
        setShowErrors(true);
        methods.handleSubmit(()=>{
            if (Object.keys(errors).length > 0) {
                return;
            }
            onContinue();
        }, ()=>undefined)();
    }, [
        errors,
        methods,
        onContinue
    ]);
    useImperativeHandle(submitRef, ()=>({
            submit: handleSubmit
        }), [
        handleSubmit
    ]);
    return /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, methods), {
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.root,
            "data-testid": testIds.BusinessDetailsAStepTestIds.Root(),
            children: [
                /*#__PURE__*/ _jsx(TextField, {
                    collapseHelperText: false,
                    value: values.companyName,
                    onChange: (e)=>onChange(_object_spread_props(_object_spread({}, values), {
                            companyName: e.target.value
                        })),
                    label: t.companyNameLabel(),
                    testId: testIds.BusinessDetailsAStepTestIds.CompanyNameField(),
                    error: showErrors && errors.companyName != null,
                    helperText: showErrors ? errors.companyName : undefined,
                    bi: {
                        name: 'company_name'
                    }
                }),
                /*#__PURE__*/ _jsx(Controller, {
                    control: methods.control,
                    name: "postalAddress",
                    render: (_renderProps)=>/*#__PURE__*/ _jsx(PostalAddressForm, {
                            testId: testIds.BusinessDetailsAStepTestIds.PostalAddressForm()
                        })
                }),
                /*#__PURE__*/ _jsx(USPhoneNumberInput, {
                    collapseHelperText: false,
                    value: watched.phoneNumber,
                    onChange: (e)=>methods.setValue('phoneNumber', e.target.value),
                    label: t.phoneLabel(),
                    testId: testIds.BusinessDetailsAStepTestIds.PhoneField(),
                    error: showErrors && errors.phoneNumber != null,
                    helperText: showErrors ? errors.phoneNumber : undefined,
                    bi: {
                        name: 'phone'
                    }
                })
            ]
        })
    }));
};
